import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../../components/about/AboutSideNav';
import { updateSEO, seoData } from '../../../../utils/seo';

// ===== Mission & Philosophy (EN mirror) — full rebuild, Implementer C scope =====
// Mirrors the JA mission page design language (navy #1a2942 / brass #a38e65 / BTN-08 / AboutSideNav /
//   gold-accent headings / 3-line leads / new variations with derivation notes). Facts from the same
//   canon (mng-thebutler/00_business-overview + current JA mission working tree = internal-completion text).
//   No invented figures; no revived external claims. EN pages contain no Japanese characters (SEO rule 1-3).

export default function MissionPageEN() {
  useEffect(() => {
    updateSEO(seoData['/en/about/mission']);
    document.documentElement.lang = 'en';
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en/' },
    { label: 'About Us', href: '/en/about' },
    { label: 'Mission' }
  ];

  const aboutNav = [
    { label: 'Overview', href: '/en/about/overview' },
    { label: 'Mission', href: '/en/about/mission' },
    { label: 'History', href: '/en/about/history' },
    { label: 'Organization', href: '/en/about/organization' },
    { label: 'Activities', href: '/en/about/activities' },
    { label: 'Access', href: '/en/about/access' }
  ];

  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const missionItems = [
    { no: '01', en: 'Systematization', ja: 'Systematizing Knowledge & Skill', desc: 'We systematize butler knowledge and skills within the organization, building a foundation for delivering high-quality service consistently.' },
    { no: '02', en: 'Expertise', ja: 'Advancing Expertise', desc: 'Through internal training systems, we continually raise the expertise of each and every butler.' },
    { no: '03', en: 'Development', ja: 'Promoting International Growth', desc: 'Through butler training at home and abroad, we promote the international development of Japan\'s distinctive butler culture.' },
    { no: '04', en: 'Society', ja: 'Contributing to Society', desc: 'We spread the professionalism and mindset of the butler throughout society, contributing to higher quality across the service industry.' },
  ];

  const visionItems = [
    { label: 'Established Social Standing', text: 'Butlers are established as socially respected professionals, active across a diversity of fields.' },
    { label: 'International Recognition', text: 'The traditional butler, fused with Japan\'s distinctive culture, is recognized internationally.' },
    { label: 'A Spirit that Permeates', text: 'The butler\'s spirit is widely understood, and the heart of hospitality takes root throughout society.' },
    { label: 'A Global Network', text: 'The association stands as a central presence in butler culture, part of a network that reaches the world.' },
  ];

  const pillars = [
    {
      en: 'Development', ja: 'Training & Development',
      img: '/images/gen2/jba-mission-training-butlers-01.jpg', aspect: 'aspect-video',
      alt: 'Japanese butler trainees receiving posture and service-conduct instruction, cultivating both technique and spirit',
      desc: 'We cultivate people who can deliver high-quality service, developing both the technical and the spiritual sides of the butler.',
      items: ['Systematic training programs', 'Practical skills training', 'A mentorship system', 'Ongoing learning support'],
    },
    {
      en: 'Heritage', ja: 'Cultural Inheritance',
      img: '/images/generated/jba-fs-013-about_mission-v3.webp', aspect: 'aspect-[3/4]', fit: 'object-cover',
      alt: 'A historic estate and furnishings, the lineage of butler culture handed down over 135 years',
      desc: 'We preserve the traditions of butler culture cultivated over 135 years of history and pass them faithfully to the next generation.',
      items: ['Preserving traditional techniques', 'Managing historical archives', 'Advancing cultural research', 'Publishing and outreach'],
    },
    {
      en: 'International', ja: 'International Exchange',
      img: '/images/gen2/jba-mission-guest-reception-en-01.jpg', aspect: 'aspect-video',
      alt: 'A Japanese butler welcoming overseas guests in a refined service setting, supporting international hospitality training',
      desc: 'We cultivate an international sensibility able to serve guests from overseas, contributing to butler culture from a global perspective.',
      items: ['Cultivating international sensibility', 'Advancing cross-cultural understanding', 'Researching cultures abroad', 'Studying international standards'],
    },
  ];

  const promises = [
    { t: 'The Highest Quality of Service', d: 'We always pursue the highest quality of service and deliver value that exceeds expectations.' },
    { t: 'Continuous Growth & Learning', d: 'We adapt to changing times and cherish the attitude of always continuing to learn.' },
    { t: 'Ethical Conduct', d: 'We hold high ethical standards and take care to act with sincerity.' },
    { t: 'Respect for Tradition', d: 'We respect the tradition and dignity of the butler and strive to carry it forward.' },
    { t: 'International Cooperation', d: 'Through exchange with butler cultures around the world, we deepen mutual understanding.' },
    { t: 'Contribution to Society', d: 'We spread the butler\'s spirit through society and contribute to building a better world.' },
  ];

  return (
    <Layout>

      {/* Hero — full-width billboard (BB-02 style, derived from JA/overview hero) */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/gen2/jba-mission-discreet-service-en-01.jpg"
          alt="Illustrative Japanese butler quietly arranging tea service, expressing discreet hospitality without depicting a real person"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Mission &amp; Philosophy</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">The Finest Hospitality</span>
              <span className="block">Lives in the Unseen</span>
            </h1>
            <div className="space-y-5">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                Building on more than 135 years of Japanese butler culture, the Japan Butler Association is dedicated to spreading the heart of hospitality and professionalism through society.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                What we pursue is a quiet, assured form of service that fulfills a wish before it is even noticed.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* Body = side nav + content (2 columns) */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            <AboutSideNav items={aboutNav} activeHref="/en/about/mission" eyebrow="About Us" heading="About the Association" />

            <div className="min-w-0 space-y-16 sm:space-y-20">

              {/* I. Philosophy lead + editorial data row (derived from overview S1) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Our Philosophy</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  The essence of a butler lies in <span className="text-[#a38e65]">the unseen</span>
                </h2>
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                  <div className="space-y-5">
                    <p>
                      The Japan Butler Association seeks to make the Japanese butler culture, nurtured over more than 135 years, the foundation of a more refined society.
                    </p>
                    <p>
                      What we hold most dear are two invisible values that every butler carries: the heart of hospitality and true professionalism.
                    </p>
                  </div>
                  <p>
                    The work of a butler is never about showing off. Sensing a wish before it is spoken and moving as naturally as air — this anticipatory service is the finest form of hospitality we pursue.
                  </p>
                  <p>
                    To systematize this philosophy within the organization and hand it faithfully to the next generation: this, we believe, is the role we must fulfill as Japan&apos;s only dedicated butler institution.
                  </p>
                </div>
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Founded</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">1890</dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Heritage</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">135<span className="text-sm text-[#8f7a56] ml-1">yrs</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Mission</dt>
                    <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">Inheritance</dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Value</dt>
                    <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">Anticipation</dd>
                  </div>
                </dl>
              </section>

              {/* II. Concept — full-bleed navy quote (new variation ①: SEC-N01 x SEC-N08) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Our Concept</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Japanese hospitality, fused with the Western butler
                </h2>
                <div className="relative overflow-hidden">
                  <img
                    src="/images/salon0926/20260912_095001.jpg"
                    alt="A salon room with a fireplace, candelabra and sofa"
                    className="absolute inset-0 w-full h-full object-cover object-[center_75%]"
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(16,20,26,0.82)_0%,rgba(16,20,26,0.88)_100%)]"></div>
                  <div className="relative z-10 px-7 sm:px-12 lg:px-16 py-14 sm:py-16 lg:py-20">
                    <div className="text-[#d7b779] font-['Playfair_Display',serif] text-5xl leading-none mb-4">“</div>
                    <blockquote className="jba-display font-normal text-[#f8f6f0] text-xl sm:text-2xl lg:text-[30px] leading-[1.7] tracking-[0.02em] max-w-3xl">
                      Sensing a need before the master gives it words, and moving as naturally as air. This anticipatory service is what we consider the essence of the butler.
                    </blockquote>
                    <div className="w-11 h-px bg-[#a38e65] mt-8 mb-6"></div>
                    <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#ded8cc] leading-[1.95] max-w-2xl">
                      The discipline and dignity of the British style, and Japan&apos;s refined etiquette. When the two are fused, a form of butler service is born — one that supports both daily life and business, far beyond the simple substitution of chores.
                    </p>
                  </div>
                </div>
              </section>

              {/* III. Mission — large numbered editorial (emphasis fonts) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Mission</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  Our <span className="text-[#a38e65]">four missions</span>
                </h2>
                <div className="space-y-5 mb-9">
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    To translate our philosophy into everyday practice, the association upholds four missions: to systematize knowledge and skill, to advance expertise, to grow the culture internationally, and to raise the quality of service across society.
                  </p>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    Together they make the value of the butler&apos;s profession something certain.
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                  {missionItems.map((m) => (
                    <div key={m.no} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-4xl leading-none shrink-0 pt-1">{m.no}</span>
                      <div>
                        <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{m.en}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-[22px] mb-2.5 leading-[1.5]">{m.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{m.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* IV. Vision — navy value grid 2x2 */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Vision</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  The future we envision
                </h2>
                <div className="bg-[#1a2942] px-7 sm:px-10 lg:px-14 py-12 sm:py-14">
                  <div className="space-y-5 mb-10">
                    <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl">
                      Beyond our mission lies the future of the butler we imagine. Butlers respected by society as professionals, a distinctly Japanese butler widely recognized, and the heart of hospitality rooted in everyday life.
                    </p>
                    <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl">
                      We draw that landscape closer, one certain step at a time.
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-10 gap-y-9">
                    {visionItems.map((v, i) => (
                      <div key={v.label} className="border-t border-[#a38e65]/40 pt-5">
                        <div className="flex items-baseline gap-3 mb-2.5">
                          <span className="font-['Playfair_Display',serif] italic text-[#d7b779] text-sm">{`0${i + 1}`}</span>
                          <h3 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl">{v.label}</h3>
                        </div>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#ded8cc] leading-[1.9]">{v.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* V. Three Pillars — three-column image cards (SEC-N05) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Three Pillars</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  The three pillars that carry our philosophy
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  Our philosophy and mission take shape through concrete daily work. Developing people, preserving and passing on tradition, and connecting with the world. These three pillars support one another and carry butler culture forward.
                </p>
                <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                  {pillars.map((p) => (
                    <div key={p.ja} className="border border-[#e2ddd2] bg-white flex flex-col">
                      <img src={p.img} alt={p.alt} className={`w-full h-48 ${(p as { fit?: string }).fit ?? 'object-cover'}`} />
                      <div className="p-6 sm:p-7 flex flex-col flex-1">
                        <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{p.en}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-[22px] mb-3 leading-[1.4]">{p.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9] mb-5">{p.desc}</p>
                        <ul className="mt-auto space-y-2 border-t border-[#e2ddd2] pt-4">
                          {p.items.map((it) => (
                            <li key={it} className="flex items-start gap-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.7]">
                              <span className="text-[#a38e65] text-[10px] leading-none pt-1.5" aria-hidden="true">◆</span>
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* VI. Our Promise — cross-ruled grid (new variation ②) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Our Promise</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  Our promise
                </h2>
                <div className="space-y-5 mb-9">
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    To realize our philosophy, mission, and vision, the association and its butlers uphold these six commitments in daily conduct: quality, growth, ethics, tradition, cooperation, and contribution to society.
                  </p>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    None of them are flashy — yet trust is built from nothing but the accumulation of such promises.
                  </p>
                </div>
                <div className="border-t border-l border-[#e2ddd2] grid sm:grid-cols-2 lg:grid-cols-3">
                  {promises.map((p, i) => (
                    <div key={p.t} className="border-r border-b border-[#e2ddd2] p-6 sm:p-7">
                      <div className="flex items-baseline gap-3 mb-2.5">
                        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-lg">{`0${i + 1}`}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.4]">{p.t}</h3>
                      </div>
                      <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{p.d}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* VII. Next Steps — navy CTA band */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VII. Next Steps</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  For those who share our philosophy
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                    If our journey of inheriting and developing butler culture resonates with you, we invite you to follow the association&apos;s work. Through our certifications, training, and butler staffing service, you can experience this philosophy firsthand.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="/en/qualifications" className={BTN08_ON_NAVY}>Certifications</a>
                    <a href="/en/seminars" className={BTN08_ON_NAVY}>Seminars &amp; Training</a>
                    <a href="/en/contact" className={BTN08_ON_NAVY}>Contact Us</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
